import { useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Row,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  CopyOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import moment from "moment";
import MyPage from "../../../../../../components/pages/Page";
import { useFetch } from "../../../../../../hooks/useFetch";

const { RangePicker } = DatePicker;
const QRY_FMT = "DD-MM-YYYY";

const buildQuery = (range) => {
  const params = new URLSearchParams();
  const [start, end] = range || [];
  if (start) params.append("tanggalAwal", start.format(QRY_FMT));
  if (end) params.append("tanggalAkhir", end.format(QRY_FMT));
  return `api/v1/cek_data/terkirim?${params.toString()}`;
};

export default () => {
  const [range, setRange] = useState([moment().subtract(2, "days"), moment()]);
  const [query, setQuery] = useState(() =>
    buildQuery([moment().subtract(2, "days"), moment()])
  );
  const [keyword, setKeyword] = useState("");
  const [rows, loading] = useFetch(query);

  const onRangeChange = (dates) => {
    setRange(dates);
    if (dates?.[0] && dates?.[1]) setQuery(buildQuery(dates));
  };

  const doRefresh = () => {
    setQuery(`${buildQuery(range)}&t=${Date.now()}`);
  };

  // respons API (payload.data): { "<doc-type>": { jumlah, referenceNumber: [...] } }
  const groups = rows && typeof rows === "object" ? Object.entries(rows) : [];
  const kw = keyword.trim().toLowerCase();

  return (
    <MyPage title="Cek Data Terkirim">
      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <RangePicker
              style={{ width: "100%", maxWidth: 320 }}
              format="DD-MM-YYYY"
              value={range}
              onChange={onRangeChange}
              prefixIcon={
                <CalendarOutlined style={{ color: "#64748b", fontSize: 12 }} />
              }
              suffixIcon={
                <CalendarOutlined style={{ color: "#64748b", fontSize: 12 }} />
              }
              separator=" - "
            />
          </Col>
          <Col flex="auto">
            <Input
              placeholder="Cari reference number..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              allowClear
            />
          </Col>
          <Col>
            <Button icon={<ReloadOutlined />} onClick={doRefresh} loading={loading}>
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {groups.length === 0 && !loading ? (
        <Card>
          <Typography.Text type="secondary">
            Tidak ada data terkirim pada periode ini. Ubah filter tanggal
            untuk menampilkan data lain.
          </Typography.Text>
        </Card>
      ) : (
        groups.map(([docType, info]) => {
          const allRefs = Array.isArray(info?.referenceNumber)
            ? info.referenceNumber
            : [];
          const filtered = kw
            ? allRefs.filter((r) => String(r).toLowerCase().includes(kw))
            : allRefs;
          const dataSource = filtered.map((ref, i) => ({
            key: `${docType}-${i}`,
            no: i + 1,
            ref,
          }));

          return (
            <Card
              key={docType}
              style={{ marginBottom: 16 }}
              title={
                <span>
                  Data Terkirim{" "}
                  <Tag color="green" style={{ marginLeft: 8 }}>
                    {info?.jumlah ?? allRefs.length} terkirim
                  </Tag>
                </span>
              }
              extra={
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Menampilkan {filtered.length} dari {allRefs.length} reference
                  number
                </Typography.Text>
              }
              loading={loading}
            >
              <Table
                size="small"
                bordered
                dataSource={dataSource}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50", "100"],
                  showTotal: (t) => `Total ${t} data`,
                }}
                columns={[
                  {
                    title: "No",
                    dataIndex: "no",
                    key: "no",
                    width: 70,
                    align: "center",
                  },
                  {
                    title: "Reference Number",
                    dataIndex: "ref",
                    key: "ref",
                    render: (v) => (
                      <Typography.Text code copyable={false}>
                        {v}
                      </Typography.Text>
                    ),
                  },
                  {
                    title: "Aksi",
                    key: "aksi",
                    width: 90,
                    align: "center",
                    render: (_, record) => (
                      <Tooltip title="Salin">
                        <Button
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => {
                            if (navigator?.clipboard) {
                              navigator.clipboard.writeText(record.ref);
                            }
                          }}
                        />
                      </Tooltip>
                    ),
                  },
                ]}
                scroll={{ x: "max-content" }}
              />
            </Card>
          );
        })
      )}
    </MyPage>
  );
};
